import { unstable_batchedUpdates } from "react-dom";
import { useTranslation } from "react-i18next";

import { SupportedLanguages } from "~/providers/LanguageProvider";

import {
    SEARCH_TABS,
    SOLR_TOPICS_METADATA_KEY,
    NormalizedTopic,
    SolrMetaGraphicsParams,
    SolrMetaReportsParams,
    SolrTopicSearchType,
    SolrMetaTopicsKeys
} from "~/types/search.types";
import { SearchActions } from "~/types/actions.enum";

import { fetchSolrSearchMeta } from "~/services/api/metadata";
import { useTopicStore, useHelperStore } from "~/stores";
import { TabKeys } from "~/stores/tab/tab.state";
import useFetchFilterData from "~/hooks/useFetchFilterData";

const useSolrTopicsCounter = () => {
    const dispatchHelper = useHelperStore(state => state.dispatch);
    const dispatchTopics = useTopicStore(state => state.dispatch);
    const { fetchYears } = useFetchFilterData();
    const { i18n } = useTranslation();

    /**
     * Fetches the total number of documents per Disclosure/Category/Topic
     * @param topicsDetailsParams
     * @param language
     * @param searchType
     */
    const fetchSolrTopicsCount = async <T extends SolrMetaGraphicsParams[] & SolrMetaReportsParams[]>(
        topicsDetailsParams: T,
        language: SupportedLanguages,
        searchType: SolrTopicSearchType
    ): Promise<Record<number, number> | null> => {
        try {
            const requests = [];
            const topicsMetaDataKey = SOLR_TOPICS_METADATA_KEY[searchType] as SolrMetaTopicsKeys;
            if (!topicsMetaDataKey) return null;

            for (let i = 0; i < topicsDetailsParams.length; i++) {
                // Build payload like useSolrSearch
                const payload = {
                    ...topicsDetailsParams[i],
                    LanguageCode: language,
                    RowsPerPage: topicsDetailsParams[i].RowsPerPage || 0,
                    CurrentPage: topicsDetailsParams[i].CurrentPage || 0
                };

                requests.push(
                    fetchSolrSearchMeta({
                        data: payload,
                        language,
                        searchType
                    })
                );
            }

            const responses = await Promise.all(requests);

            // Map topic ID to numFound
            const topicsCounter: Record<number, number> = topicsDetailsParams.reduce(
                (acc: Record<number, number>, item, index) => {
                    const topicData = responses[index].data.response;
                    const topicId = (item as Record<string, any>)[topicsMetaDataKey]?.[0] ?? -1;
                    if (topicId >= 0) acc[topicId] = topicData.numFound;
                    return acc;
                },
                {}
            );

            return topicsCounter;
        } catch (err: any) {
            console.error(err.message);
            return null;
        }
    };

    /**
     * Fetches the total number of documents per Disclosure/Category/Topic and records it in the store.
     * Also sets the global store loading and app initialized flags after the data fetch.
     */
    const fetchSolrTopicsDocsCount = async (
        metaTopics: NormalizedTopic[],
        tab: TabKeys
    ): Promise<Record<number, number> | null> => {
        dispatchHelper({ type: SearchActions.SET_IS_LOADING, payload: true });

        const searchType =
            tab === SEARCH_TABS.IMAGES ? SolrTopicSearchType.IMAGE : SolrTopicSearchType.PAGE;

        const topicsMetaDataKey = tab === SEARCH_TABS.IMAGES
            ? SOLR_TOPICS_METADATA_KEY[SolrTopicSearchType.IMAGE]
            : SOLR_TOPICS_METADATA_KEY[SolrTopicSearchType.PAGE];

        // Construct payloads like useSolrSearch
        const topicsParams: SolrMetaReportsParams[] = await Promise.all(
            metaTopics.map(async (topic) => {
                const { disclosureId, categoryId, exploreId } = topic;
                const allYearsForCategory = await fetchYears({
                    DocumentTypeID: [Number(disclosureId)],
                    CategoryId: [Number(categoryId)]
                });

                // fetch numFound for each year
                const validYears: number[] = [];
                for (const year of allYearsForCategory) {
                    const countPayload = {
                        DocumentTypeID: [Number(disclosureId)],
                        CategoryId: [Number(categoryId)],
                        [topicsMetaDataKey]: [Number(exploreId)],
                        RowsPerPage: 0,
                        CurrentPage: 0,
                        DocumentYear: [Number(year)],
                        LanguageCode: i18n.language
                    };
                    const response = await fetchSolrSearchMeta({
                        data: countPayload,
                        language: i18n.language as SupportedLanguages,
                        searchType
                    });

                    if (response.data.response.numFound > 0) {
                        validYears.push(Number(year));
                    }
                }

                // Take the 2 latest valid years
                const documentYears = validYears.slice(0, 2);

                return {
                    DocumentTypeID: [Number(disclosureId)],
                    CategoryId: [Number(categoryId)],
                    [topicsMetaDataKey]: [Number(exploreId)],
                    RowsPerPage: 0,
                    CurrentPage: 0,
                    DocumentYear: documentYears,
                    LanguageCode: i18n.language
                };
            })
        );

        try {
            const topicsData = await fetchSolrTopicsCount(
                topicsParams,
                i18n.language as SupportedLanguages,
                searchType
            );

            unstable_batchedUpdates(() => {
                dispatchTopics({ type: SearchActions.SET_TOPIC_COUNTS, payload: topicsData || null });
                dispatchHelper({ type: SearchActions.SET_IS_LOADING, payload: false });
            });

            return topicsData;
        } catch (error) {
            console.error(error);
            dispatchHelper({ type: SearchActions.SET_IS_LOADING, payload: false });
            return null;
        }
    };

    return {
        fetchSolrTopicsCount,
        fetchSolrTopicsDocsCount
    };
};

export default useSolrTopicsCounter;
